import { SharedFormModule } from '@agingplan';
import { HTTP_INTERCEPTORS, HttpClientModule, HttpClientXsrfModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard, MsalInterceptor, MsalModule, MsalRedirectComponent, MsalService } from '@azure/msal-angular';
import { provideNgxMetaCore } from '@davidlj95/ngx-meta/core';
import { provideNgxMetaJsonLd } from '@davidlj95/ngx-meta/json-ld';
import { provideNgxMetaRouting } from '@davidlj95/ngx-meta/routing';
import { provideNgxMetaStandard } from '@davidlj95/ngx-meta/standard';
import * as Sentry from '@sentry/angular';
import { CookieService } from 'ngx-cookie-service';
import { NgcCookieConsentModule } from 'ngx-cookieconsent';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';
import { DBConfig, NgxIndexedDBModule } from 'ngx-indexed-db';
import { environment } from 'src/environments/environment';
import '../../../../@libs/string';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FooterModule } from './components/footer/footer.module';
import { cookieConsentConfig } from './cookie-concent-config';
import { AuthenticationInterceptor } from './interceptors/authentication.interceptor';
import { MSALGuardConfigFactory, MSALInstanceFactory, MSALInterceptorConfigFactory } from './msal';

const dbConfig: DBConfig = {
  name: 'AgingplanFormsDB',
  version: 1,
  objectStoresMeta: [
    {
      store: 'forms',
      storeConfig: { keyPath: 'id', autoIncrement: true },
      storeSchema: [
        { name: 'name', keypath: 'name', options: { unique: true } },
        { name: 'data', keypath: 'data', options: { unique: false } },
        { name: 'updated', keypath: 'updated', options: { unique: false } },
      ],
    },
  ],
};

// Add the Google Analytics module if we're not localhost
const googleAnalyticsModule = [];
if (environment.google.analytics) {
  googleAnalyticsModule.push(NgxGoogleAnalyticsModule.forRoot(environment.google.analytics));
  googleAnalyticsModule.push(NgxGoogleAnalyticsRouterModule);
}

// Sentry providers
const sentryProviders = environment.production
  ? [
      { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
      { provide: Sentry.TraceService, deps: [Router] },
      {
        provide: APP_INITIALIZER,
        useFactory: () => () => {},
        deps: [Sentry.TraceService],
        multi: true,
      },
    ]
  : [];

// MSAL providers only for production
const msalProviders = environment.production
  ? [
      { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
      MsalService,
      MsalGuard,
      MsalBroadcastService,
      { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
      { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
      { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    ]
  : [];

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    SharedFormModule.forRoot(environment),
    NgxIndexedDBModule.forRoot(dbConfig),
    NgcCookieConsentModule.forRoot(cookieConsentConfig),
    FooterModule,
    HttpClientModule,
    HttpClientXsrfModule,
    ...googleAnalyticsModule,
    ...(environment.production ? [MsalModule] : []),
  ],
  providers: [
    // Sentry providers
    ...sentryProviders,
    // HTTP interceptors
    { provide: HTTP_INTERCEPTORS, useClass: AuthenticationInterceptor, multi: true },
    // MSAL providers (only in production)
    ...msalProviders,
    // Other services
    CookieService,
    // Structured data
    provideNgxMetaCore(),
    provideNgxMetaRouting(),
    provideNgxMetaJsonLd(),
    provideNgxMetaStandard(),
  ],
  bootstrap: [AppComponent, ...(environment.production ? [MsalRedirectComponent] : [])],
})
export class AppModule {}
